import {createContext, useContext, useState, ReactNode} from 'react';
import AurToast from '../../components/common/aur-toast/AurToast';
import {GAToastTrackingInfo} from '../../types/analyticsTypes';

export type showToastType = (
  message: string,
  severity?: 'success' | 'error' | 'warning',
  hideDuration?: number,
  horizontalPosition?: 'center' | 'left' | 'right',
  verticalPosition?: 'bottom' | 'top',
  gaTrackingInfo?: GAToastTrackingInfo
) => void;

type ToastContextType = {
  showToast: showToastType;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastDuration, setToastDuration] = useState(3000);
  const [toastSeverity, setToastSeverity] = useState<
    'success' | 'error' | 'warning'
  >('success');
  const [horizontalPosition, setHorizontalPosition] = useState<
    'center' | 'left' | 'right'
  >('right');
  const [verticalPosition, setVerticalPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const [gaTrackingData, setGaTrackingData] = useState<
    GAToastTrackingInfo | undefined
  >(undefined);

  const showToast = (
    message: string,
    severity: 'success' | 'error' | 'warning' = 'success',
    hideDuration = 3000,
    horizontalPosition: 'center' | 'left' | 'right' = 'right',
    verticalPosition: 'bottom' | 'top' = 'bottom',
    gaTrackingInfo?: GAToastTrackingInfo
  ) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastDuration(hideDuration);
    setToastOpen(true);
    setHorizontalPosition(horizontalPosition);
    setVerticalPosition(verticalPosition);
    setGaTrackingData(gaTrackingInfo);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  return (
    <ToastContext.Provider value={{showToast}}>
      {children}
      <AurToast
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
        hideDuration={toastDuration}
        horizontalPosition={horizontalPosition}
        verticalPosition={verticalPosition}
        gaTrackingInfo={gaTrackingData}
      />
    </ToastContext.Provider>
  );
};
